import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import {
  PROJECTS, LENDERS, CONTRACTORS, PERMIT_OPTIONS, RESPONSES, CRUNCH_OPTIONS,
  PROJECT_DOCUMENTS, BUDGET_CATEGORIES,
  type ProjectKey, type LenderKey, type ContractorKey, type PermitKey,
  type ResponseKey, type CrunchKey, type BudgetCategory,
} from "@/data/developmentSim";
import KeyTermsButton from "@/components/development/KeyTermsSheet";
import LiveDashboard, { type DashboardState } from "@/components/development/LiveDashboard";
import DocumentReview from "@/components/development/DocumentReview";
import BudgetBoard from "@/components/development/BudgetBoard";
import {
  ArrowRight, Building2, HardHat, Clock, AlertTriangle, CheckCircle2,
  DollarSign, TrendingUp, Hammer, Compass, FileSearch, ClipboardList,
  Wrench, Stamp, Receipt, FlagTriangleRight, Crown,
} from "lucide-react";
import { useRef } from "react";
import {
  newAttemptId, startModuleAttempt, commitModuleAttempt, trackModule, trackAccountEffect,
} from "@/lib/learning/telemetry";
import { DEVELOPMENT_EVENTS as DEV, MODULE_CONCEPT_TAGS } from "@/lib/learning/moduleEvents";

/* ─────────────────────────────  Steps  ───────────────────────── */

type Step =
  | "intro" | "brief" | "opportunities" | "select" | "due_diligence"
  | "financing" | "budget" | "contractor" | "permitting" | "event"
  | "response" | "cash_check" | "ledger" | "exit" | "report"
  | "quiz" | "summary";

const STEPS: Step[] = [
  "intro","brief","opportunities","select","due_diligence","financing",
  "budget","contractor","permitting","event","response","cash_check",
  "ledger","exit","report","quiz","summary",
];

const STARTING_CASH = 650_000;

/* ─────────────────────────  Choice list helper  ────────────────── */

interface OptCard<T extends string> { key: T; label: string; summary?: string; bullets: string[] }

function ChoiceList<T extends string>({
  options, value, onChange,
}: { options: OptCard<T>[]; value: T | null; onChange: (v: T) => void }) {
  return (
    <div className="grid gap-3">
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button key={o.key} onClick={() => onChange(o.key)}
            className={`rounded-2xl border p-4 text-left transition-all ${
              active ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"
            }`}>
            <p className="font-heading text-sm font-bold text-foreground">{o.label}</p>
            {o.summary && <p className="mt-1 text-xs text-muted-foreground">{o.summary}</p>}
            <ul className="mt-2 space-y-1">
              {o.bullets.map((b, i) => <li key={i} className="text-[11px] text-foreground/75">• {b}</li>)}
            </ul>
          </button>
        );
      })}
    </div>
  );
}

function Row({ label, value, bold, valueClass }: { label: string; value: string; bold?: boolean; valueClass?: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className={`text-muted-foreground ${bold ? "font-heading font-bold text-foreground" : ""}`}>{label}</span>
      <span className={`font-mono ${bold ? "font-heading font-bold text-foreground" : "text-foreground"} ${valueClass ?? ""}`}>{value}</span>
    </div>
  );
}

const Disclaimer = () => (
  <p className="text-[10px] text-muted-foreground/70 italic text-center">
    Educational simulation. Simplified estimates only — not financial, legal, or lending advice.
  </p>
);

/* ─────────────────────────  Default allocation  ─────────────── */

const DEFAULT_ALLOC: Record<BudgetCategory, number> = {
  hard_costs: 55, soft_costs: 8, permits_legal: 5,
  carrying_costs: 10, contingency: 10, sale_prep: 12,
};

/* ─────────────────────────  Main component  ─────────────────── */

const DevelopmentSimulator = () => {
  const navigate = useNavigate();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();

  const [step, setStep] = useState<Step>("intro");
  const [projectIdx, setProjectIdx] = useState<number | null>(null);
  const [docResolved, setDocResolved] = useState<Record<string, boolean>>({});
  const [dd, setDD] = useState<"skip"|"basic"|"full"|"seller"|null>(null);
  const [lender, setLender] = useState<LenderKey | null>(null);
  const [alloc, setAlloc] = useState<Record<BudgetCategory, number>>(DEFAULT_ALLOC);
  const [contractor, setContractor] = useState<ContractorKey | null>(null);
  const [permit, setPermit] = useState<PermitKey | null>(null);
  const [resp, setResp] = useState<ResponseKey | null>(null);
  const [crunch, setCrunch] = useState<CrunchKey | null>(null);
  const [exitChoice, setExitChoice] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const attemptRef = useRef(newAttemptId("development"));
  const startedRef = useRef(false);
  const startTelemetry = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    void startModuleAttempt({
      moduleSlug: "development", attemptId: attemptRef.current,
      conceptTags: MODULE_CONCEPT_TAGS.development,
    });
  };

  // Module-event telemetry — emit once per choice transition.
  useEffect(() => { if (projectIdx === null) return; startTelemetry();
    void trackModule({ eventType: DEV.PROJECT_SELECTED, moduleSlug: "development", attemptId: attemptRef.current,
      metadata: { project_type: project?.key, project_cost: totalProjectCost, estimated_value: project?.expectedValue ?? null } });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [projectIdx]);
  useEffect(() => { if (!dd) return;
    void trackModule({ eventType: DEV.DUE_DILIGENCE, moduleSlug: "development", attemptId: attemptRef.current, metadata: { decision_choice: dd, ddCost } });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [dd]);
  useEffect(() => { if (!lender) return;
    void trackModule({ eventType: DEV.FINANCING, moduleSlug: "development", attemptId: attemptRef.current, metadata: { decision_choice: lender, ltc: lenderOffer?.ltc, interest_rate: lenderOffer?.rate, loan_amount: loanAmount, equity_required: equityRequired } });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [lender]);
  useEffect(() => { if (!contractor) return;
    void trackModule({ eventType: DEV.CONTRACTOR, moduleSlug: "development", attemptId: attemptRef.current, metadata: { decision_choice: contractor } });
  }, [contractor]);
  useEffect(() => { if (!permit) return;
    void trackModule({ eventType: DEV.PERMIT, moduleSlug: "development", attemptId: attemptRef.current, metadata: { decision_choice: permit } });
  }, [permit]);
  useEffect(() => { if (!resp) return;
    void trackModule({ eventType: DEV.EVENT_RESOLVED, moduleSlug: "development", attemptId: attemptRef.current, metadata: { decision_choice: resp, contingency_used: respImpact.contingencyUsed } });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [resp]);
  useEffect(() => { if (!crunch) return;
    void trackModule({ eventType: DEV.CRUNCH_RESOLVED, moduleSlug: "development", attemptId: attemptRef.current, metadata: { decision_choice: crunch } });
  }, [crunch]);
  useEffect(() => { if (!exitChoice) return;
    void trackModule({ eventType: DEV.EXIT, moduleSlug: "development", attemptId: attemptRef.current, metadata: { selected_exit_strategy: exitChoice } });
  }, [exitChoice]);

  const gate = moduleGates.find((g) => g.moduleKey === "development");
  const isLocked = !gate?.isUnlocked;

  const project = projectIdx !== null ? PROJECTS[projectIdx] : null;
  const lenderOffer = lender ? LENDERS.find((l) => l.key === lender)! : null;
  const contractorOffer = contractor ? CONTRACTORS.find((c) => c.key === contractor)! : null;
  const permitOption = permit ? PERMIT_OPTIONS.find((p) => p.key === permit)! : null;

  /* ─── Derived numbers ─── */

  const totalProjectCost = project ? project.askingPrice + project.rehabBuildCost : 0;
  const ddCost = dd === "skip" ? 0 : dd === "basic" ? 2_500 : dd === "full" ? 7_500 : 500;

  const loanAmount = lenderOffer ? Math.round(totalProjectCost * lenderOffer.ltc) : 0;
  const equityRequired = totalProjectCost - loanAmount;

  const contingencyPct = alloc.contingency / 100;
  const contingencyBudget = Math.round(totalProjectCost * contingencyPct);

  // Risk model
  const docCorrectCount = project
    ? PROJECT_DOCUMENTS[project.key].filter((d) => docResolved[d.id] === true).length
    : 0;
  const docTotalAnswered = project
    ? PROJECT_DOCUMENTS[project.key].filter((d) => d.id in docResolved).length
    : 0;

  const ddDelta = dd === "skip" ? 35 : dd === "basic" ? -10 : dd === "full" ? -25 : dd === "seller" ? 25 : 0;
  const lenderDelta = lenderOffer?.riskDelta ?? 0;
  const contractorDelta = contractorOffer?.riskDelta ?? 0;
  const permitDelta = permitOption?.riskDelta ?? 0;
  const allocRisk =
    (alloc.contingency < 5 ? 25 : alloc.contingency < 10 ? 10 : -10) +
    (alloc.permits_legal < 3 ? 20 : 0) +
    (alloc.carrying_costs < 6 ? 20 : 0) +
    (alloc.sale_prep < 6 ? 10 : 0);

  const docRisk = -docCorrectCount * 4;
  const baseRisk = project?.baseRisk ?? 0;

  const risk = Math.max(0, Math.min(150,
    baseRisk + ddDelta + lenderDelta + contractorDelta + permitDelta + allocRisk + docRisk
  ));

  const confidence = Math.max(0, Math.min(100,
    40 + docCorrectCount * 8
    + (dd === "full" ? 15 : dd === "basic" ? 5 : dd === "skip" ? -15 : -8)
    + (lender === "bank" ? 10 : lender === "balanced" ? 5 : lender === "aggressive" ? -10 : 0)
    + (contractor === "premium" ? 8 : contractor === "balanced" ? 3 : contractor === "informal" ? -15 : -5)
  ));

  /* ─── Construction event (driven by prior choices) ─── */

  const eventInfo = useMemo(() => {
    if (!project) return null;
    let title = "Permitting Delay";
    let body = "City review takes longer than expected. Carrying costs continue while permits are pending.";
    let icon: "stop" | "site" | "draw" | "tenant" | "material" | "permit" = "permit";
    if (permit === "early") { title = "Stop-Work Order"; icon = "stop";
      body = "An inspector finds you began work before permits were finalized. The city issues a stop-work order plus penalties."; }
    else if (dd === "skip" || dd === "seller") { title = "Hidden Site Condition"; icon = "site";
      body = "Crews uncover an issue your due diligence missed — older wiring, soil contamination, or undisclosed structural damage."; }
    else if (lender === "aggressive" || lender === "private") { title = "Lender Draw Delay"; icon = "draw";
      body = "Your lender slows the next construction draw, demanding extra documentation and inspection photos."; }
    else if (project.key === "mixed") { title = "Anchor Tenant Walks"; icon = "tenant";
      body = "Your anchor retail tenant backs out. Re-marketing and lease-up will take longer."; }
    else if (project.key === "luxury") { title = "Material Cost Spike"; icon = "material";
      body = "Steel and finish prices jump mid-build. The contractor submits a change order."; }

    const severity = Math.min(6, Math.max(2,
      2
      + (dd === "skip" ? 2 : dd === "seller" ? 1 : 0)
      + (alloc.contingency < 5 ? 2 : alloc.contingency < 10 ? 1 : 0)
      + (lender === "aggressive" ? 1 : 0)
      + (contractor === "informal" ? 1 : contractor === "cheap" ? 1 : 0)
      + (permit === "early" ? 2 : permit === "quick" ? 1 : 0)
      + (project.difficulty === "High" ? 1 : 0)
    ));
    const rate = lenderOffer?.rate ?? 0.08;
    const extraInterest = Math.round(loanAmount * rate * (severity / 12));
    const extraHolding = Math.round(totalProjectCost * 0.002 * severity);
    const delayCost = extraInterest + extraHolding;
    return { title, body, icon, severity, extraInterest, extraHolding, delayCost };
  }, [project, dd, lender, lenderOffer, contractor, permit, alloc, totalProjectCost, loanAmount]);

  /* ─── Response impact ─── */

  const respImpact = useMemo(() => {
    if (!eventInfo || !project) return { contingencyUsed: 0, extraDebt: 0, valueAdj: 0, extraMonths: 0, note: "" };
    const d = eventInfo.delayCost;
    switch (resp) {
      case "use_contingency":
        return { contingencyUsed: Math.min(contingencyBudget, d), extraDebt: Math.max(0, d - contingencyBudget),
          valueAdj: 0, extraMonths: 0,
          note: contingencyBudget >= d ? "Reserve absorbs the hit — buffer is now thinner."
            : "Reserve runs dry partway through. The rest hits as new debt." };
      case "borrow":
        return { contingencyUsed: 0, extraDebt: d, valueAdj: 0, extraMonths: 0,
          note: "Project stays on track but debt and interest rise." };
      case "cut_scope":
        return { contingencyUsed: 0, extraDebt: 0, valueAdj: -Math.round(project.expectedValue * 0.05),
          extraMonths: 0, note: "You save cash but the finished product appraises for less." };
      case "pause":
        return { contingencyUsed: 0, extraDebt: Math.round(d * 0.5), valueAdj: 0, extraMonths: 2,
          note: "Spending slows but carrying costs and contractor friction add up." };
      default: return { contingencyUsed: 0, extraDebt: 0, valueAdj: 0, extraMonths: 0, note: "" };
    }
  }, [resp, eventInfo, contingencyBudget, project]);

  /* ─── Live cash + ledger calc ─── */

  const monthsPlanned = project?.timelineMonths ?? 0;
  const monthsDelayed = (eventInfo?.severity ?? 0) + respImpact.extraMonths;
  const totalMonths = monthsPlanned + monthsDelayed;

  const hardCostMult = contractorOffer?.costMult ?? 1;
  const actualHardCost = project ? Math.round(project.rehabBuildCost * project.hardCostPct * hardCostMult) : 0;
  const otherBuildCost = project ? project.rehabBuildCost - Math.round(project.rehabBuildCost * project.hardCostPct) : 0;

  const interestRate = lenderOffer?.rate ?? 0;
  const baseInterest = Math.round(loanAmount * interestRate * (totalMonths / 12));
  const rescueInterest = Math.round(respImpact.extraDebt * 0.10);
  const interestAccrued = baseInterest + rescueInterest;

  const permitFees = permitOption?.cost ?? 0;
  const carryingCost = Math.round(totalProjectCost * 0.002 * totalMonths);

  // Cash flow: starting cash → minus equity, minus ddCost, minus permitFees,
  // minus any contingency used (already inside budget pool) → simplified.
  const equityDeployed = equityRequired;
  const cashSpentBeyondLoan =
    equityDeployed + ddCost + permitFees
    + Math.max(0, actualHardCost - Math.round(project?.rehabBuildCost ?? 0) * 1) // overrun above plan
    + (contractor === "informal" ? 8_000 : 0); // dispute reserve
  const cashAvailable = STARTING_CASH - cashSpentBeyondLoan;
  const cashCrunch = cashAvailable < 0 || respImpact.extraDebt > 0 && cashAvailable < 10_000;

  const crunchAdjValue = crunch === "cut_scope" ? -(project?.expectedValue ?? 0) * 0.10
    : crunch === "fire_sale" ? -(project?.expectedValue ?? 0) * 0.20 : 0;
  const crunchExtraInterest = crunch === "emergency_debt" ? 25_000 : 0;
  const partnerShare = crunch === "partner" ? 0.30 : 0;

  const qualityAdj = (contractorOffer?.qualityDelta ?? 0);
  const finalValueRaw = project
    ? Math.round(project.expectedValue * (1 + qualityAdj) + respImpact.valueAdj + crunchAdjValue)
    : 0;

  const allInCost =
    (project?.askingPrice ?? 0) + actualHardCost + otherBuildCost + ddCost + permitFees +
    interestAccrued + carryingCost + respImpact.contingencyUsed + respImpact.extraDebt + crunchExtraInterest;

  const sellingCosts = Math.round(finalValueRaw * 0.06);
  const debtPayoff = loanAmount + respImpact.extraDebt;
  const proceeds = finalValueRaw - sellingCosts;
  const grossProfit = proceeds - debtPayoff - equityDeployed;
  const profitAfterPartner = Math.round(grossProfit * (1 - partnerShare));
  const roe = equityDeployed > 0 ? (profitAfterPartner / equityDeployed) * 100 : 0;

  /* ─── Phase tracker ─── */

  const PHASE_MAP: Record<Step, string> = {
    intro: "Setup", brief: "Setup", opportunities: "Acquisition",
    select: "Acquisition", due_diligence: "Due Diligence",
    financing: "Financing", budget: "Financing", contractor: "Pre-Construction",
    permitting: "Permitting", event: "Construction", response: "Construction",
    cash_check: "Construction", ledger: "Lease-up / Sale prep",
    exit: "Exit", report: "Exit", quiz: "Debrief", summary: "Complete",
  };

  /* ─── Dashboard state ─── */

  const dashState: DashboardState = {
    phase: PHASE_MAP[step],
    cash: cashAvailable,
    loan: loanAmount,
    equityInvested: equityDeployed,
    contingencyRemaining: Math.max(0, contingencyBudget - respImpact.contingencyUsed),
    interestAccrued,
    monthsPlanned,
    monthsDelayed,
    risk,
    confidence,
    estimatedValue: finalValueRaw,
    estimatedProfit: profitAfterPartner,
  };

  /* ─── Exit availability based on actual numbers ─── */

  const exitOptions = useMemo(() => {
    if (!project) return [] as OptCard<string>[];
    const monthlyRent = Math.round(project.expectedValue * 0.006); // 0.6% rule-ish
    const monthlyDebt = Math.round(debtPayoff * (interestRate || 0.08) / 12) + Math.round(debtPayoff / 360);
    const monthlyExpenses = Math.round(monthlyRent * 0.35);
    const monthlyCashFlow = monthlyRent - monthlyDebt - monthlyExpenses;
    const opts: OptCard<string>[] = [];
    if (finalValueRaw > debtPayoff) {
      opts.push({ key: "sell", label: "Sell immediately",
        summary: `Proceeds ${money(proceeds)} cover debt ${money(debtPayoff)}.`,
        bullets: [`Net to you: ~${money(profitAfterPartner)}`, "Selling costs apply (6%)", "Fastest cash recovery"] });
    } else {
      opts.push({ key: "sell", label: "Sell immediately (weak)",
        summary: "Sale proceeds may not cover debt cleanly.",
        bullets: ["Risk of cash-out-of-pocket at close", "Consider alternatives below"] });
    }
    opts.push({ key: "hold", label: "Hold as rental",
      summary: `Est. monthly cash flow ${money(monthlyCashFlow)} (rent ${money(monthlyRent)} − debt ${money(monthlyDebt)} − ops ${money(monthlyExpenses)}).`,
      bullets: [
        monthlyCashFlow > 0 ? "Cash flow positive — viable" : "Cash flow negative — risky",
        "Tenant + vacancy + repair risk",
        "Slower return, ongoing management",
      ] });
    const refiAppraisalOK = finalValueRaw > debtPayoff * 1.25;
    opts.push({ key: "refi", label: "Refinance & hold",
      summary: refiAppraisalOK ? "Appraisal supports a cash-out refi." : "Appraisal may not support cash-out.",
      bullets: [
        refiAppraisalOK ? "Pull some equity, keep the asset" : "Refinance may be blocked",
        "Adds debt risk", "Needs DSCR > 1.2",
      ] });
    if (cashCrunch || risk >= 90 || finalValueRaw < debtPayoff) {
      opts.push({ key: "fire", label: "Sell early / fire sale",
        summary: "Project pressure forces an early exit.",
        bullets: ["Sells around 80% of expected value", "Prevents deeper losses", "Reputation cost"] });
    }
    if (equityRequired > STARTING_CASH || cashCrunch) {
      opts.push({ key: "partner", label: "Bring in equity partner",
        summary: "Trade upside for cash and survival.",
        bullets: ["Give up ~30% of profit", "Cash injection", "Keeps the project alive"] });
    }
    return opts;
  }, [project, finalValueRaw, debtPayoff, proceeds, profitAfterPartner, interestRate, cashCrunch, risk, equityRequired]);

  /* ─── Document completion gate for project select ─── */

  const allDocsReviewed = project
    ? PROJECT_DOCUMENTS[project.key].every((d) => d.id in docResolved)
    : false;

  /* ─── Steps with dashboard wrapper ─── */

  const showDashboard = !["intro", "brief", "opportunities", "select"].includes(step);

  const HeaderRow = () => (
    <div className="flex items-center justify-between gap-2">
      <KeyTermsButton />
      {showDashboard && (
        <span className="text-[10px] font-mono text-muted-foreground">
          Risk {risk} · Confidence {confidence}
        </span>
      )}
    </div>
  );

  /* ───────────────────────────  JSX  ─────────────────────────── */

  if (loading) return null;

  return (
    <ModuleShell
      title="Development Simulator"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={gate?.missingPrereqs?.length ? `Complete: ${gate.missingPrereqs.join(", ")}` : "Complete prerequisites to unlock."}
      isCompleted={completed}
      completedMessage="You ran a real development project end-to-end."
      stepKey={step}
    >
      <div className="space-y-4">
        {showDashboard && <LiveDashboard s={dashState} />}
        <HeaderRow />

        {step === "intro" && (
          <>
            <ModuleStepCard
              icon={<Building2 className="w-8 h-8 text-primary" />}
              title="Real Estate Development"
              body="You're stepping into a working developer's seat. You'll review documents, structure financing, allocate a budget, manage a contractor, handle permits, weather an event, and exit the project."
              highlights={[
                "Live project dashboard updates after every decision",
                "Review documents and find red flags",
                "Manually allocate budget by category",
                "Cash crunch is possible if you over-leverage",
              ]}
              action={<Button onClick={() => setStep("brief")} className="w-full gap-2">Let's Begin <ArrowRight className="w-4 h-4" /></Button>}
            />
            <Disclaimer />
          </>
        )}

        {step === "brief" && (
          <>
            <ModuleStepCard
              icon={<Compass className="w-8 h-8 text-primary" />}
              title="Developer Brief"
              body={`Starting capital: ${money(STARTING_CASH)}. Your job is to protect cash, manage risk, finish the project, and choose the best exit. Every decision moves the dashboard.`}
              highlights={[
                "Finish the project without running out of cash",
                "Use leverage carefully",
                "Plan for surprises before they happen",
                "Pick an exit that fits the actual numbers",
              ]}
              action={<Button onClick={() => setStep("opportunities")} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>}
            />
            <Disclaimer />
          </>
        )}

        {step === "opportunities" && (
          <>
            <ModuleStepCard
              icon={<FileSearch className="w-8 h-8 text-primary" />}
              title="Review Project Opportunities"
              body="Three deals are on the table. Tap a project to open its document file. Find the red flag in each document before you commit."
              action={<div />}
            />
            <div className="grid gap-3">
              {PROJECTS.map((p, i) => {
                const docs = PROJECT_DOCUMENTS[p.key];
                const reviewedCount = docs.filter((d) => d.id in docResolved).length;
                const correct = docs.filter((d) => docResolved[d.id] === true).length;
                return (
                  <div key={p.key} className="rounded-2xl border border-border bg-card/60 p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-heading text-sm font-bold text-foreground">{p.name}</p>
                        <p className="text-[11px] text-muted-foreground">{p.type}</p>
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                        {reviewedCount}/{docs.length} reviewed · {correct} flagged
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground/75">{p.surfaceSummary}</p>
                    <div className="grid grid-cols-2 gap-y-1 text-[11px] text-foreground/80">
                      <span>Asking: <span className="font-mono">{money(p.askingPrice)}</span></span>
                      <span>Build: <span className="font-mono">{money(p.rehabBuildCost)}</span></span>
                      <span>Est. value: <span className="font-mono">{money(p.expectedValue)}</span></span>
                      <span>Timeline: <span className="font-mono">{p.timelineMonths} mo</span></span>
                    </div>
                    <DocumentReview
                      projectKey={p.key}
                      resolved={docResolved}
                      onResolve={(id, correct) => setDocResolved((r) => ({ ...r, [id]: correct }))}
                    />
                    <Button
                      size="sm" variant={projectIdx === i ? "default" : "outline"}
                      onClick={() => setProjectIdx(i)} className="w-full mt-1"
                    >
                      {projectIdx === i ? "Selected" : "Select this project"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <Button onClick={() => setStep("select")} disabled={projectIdx === null || !allDocsReviewed}
              className="w-full gap-2">
              {allDocsReviewed ? "Continue" : "Review all docs for selected project"} <ArrowRight className="w-4 h-4" />
            </Button>
            <Disclaimer />
          </>
        )}

        {step === "select" && project && (
          <>
            <ModuleStepCard
              icon={<CheckCircle2 className="w-8 h-8 text-primary" />}
              title={`Committed: ${project.name}`}
              body={`You found ${docCorrectCount} of ${PROJECT_DOCUMENTS[project.key].length} red flags. Confidence going in: ${confidence}. ${project.teaches}`}
              action={<Button onClick={() => setStep("due_diligence")} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>}
            />
            <Disclaimer />
          </>
        )}

        {step === "due_diligence" && project && (
          <>
            <ModuleStepCard
              icon={<ClipboardList className="w-8 h-8 text-primary" />}
              title="Due Diligence Depth"
              body="How deeply do you investigate before closing? Skipping saves cash now and takes on hidden problems later."
              action={<div />}
            />
            <ChoiceList<"skip"|"basic"|"full"|"seller"> value={dd} onChange={setDD} options={[
              { key: "skip", label: "Skip diligence — move fast", summary: "Saves cash now.", bullets: ["Risk +35", "Surprise repairs likely", "No upfront cost"] },
              { key: "basic", label: "Basic inspection + budget review", summary: "Modest cost.", bullets: ["Cost +$2,500", "Risk -10", "Catches obvious issues"] },
              { key: "full", label: "Full due diligence package", summary: "Most thorough.", bullets: ["Cost +$7,500", "Risk -25", "Strongest underwriting"] },
              { key: "seller", label: "Trust the seller's numbers", summary: "Cheapest, weakest info.", bullets: ["Risk +25", "Estimate accuracy drops", "Contingency need rises"] },
            ]} />
            <Button onClick={() => setStep("financing")} disabled={!dd} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "financing" && project && (
          <>
            <ModuleStepCard
              icon={<DollarSign className="w-8 h-8 text-primary" />}
              title="Lender Term Sheets"
              body="Compare four real-world-style loan offers. Each shifts your cash, your monthly carry, and your risk."
              action={<div />}
            />
            <div className="grid gap-3">
              {LENDERS.map((l) => {
                const active = lender === l.key;
                const loan = Math.round(totalProjectCost * l.ltc);
                const equity = totalProjectCost - loan;
                const monthly = Math.round(loan * l.rate / 12);
                return (
                  <button key={l.key} onClick={() => setLender(l.key)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      active ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-heading text-sm font-bold text-foreground">{l.name}</p>
                      <span className="text-[10px] font-mono text-muted-foreground">{l.approvalDays}d approval</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-y-1 text-[11px] text-foreground/80">
                      <span>LTC: <span className="font-mono">{(l.ltc*100).toFixed(0)}%</span></span>
                      <span>Rate: <span className="font-mono">{(l.rate*100).toFixed(2)}%</span></span>
                      <span>Loan: <span className="font-mono">{money(loan)}</span></span>
                      <span>Equity: <span className="font-mono">{money(equity)}</span></span>
                      <span className="col-span-2">Monthly carry: <span className="font-mono">{money(monthly)}</span></span>
                    </div>
                    <ul className="mt-2 space-y-0.5">
                      {l.bullets.map((b, i) => <li key={i} className="text-[11px] text-foreground/75">• {b}</li>)}
                    </ul>
                  </button>
                );
              })}
            </div>
            <Button onClick={() => setStep("budget")} disabled={!lender} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "budget" && project && (
          <>
            <ModuleStepCard
              icon={<Hammer className="w-8 h-8 text-primary" />}
              title="Allocate the Budget"
              body="You control how the project pool is split. Under-fund the wrong line and events will punish you later."
              action={<div />}
            />
            <BudgetBoard pool={totalProjectCost} allocation={alloc} onChange={setAlloc} />
            <Button
              onClick={() => setStep("contractor")}
              disabled={(Object.values(alloc) as number[]).reduce((a, b) => a + b, 0) !== 100}
              className="w-full gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
            <Disclaimer />
          </>
        )}

        {step === "contractor" && project && (
          <>
            <ModuleStepCard
              icon={<Wrench className="w-8 h-8 text-primary" />}
              title="Choose Your Contractor"
              body="Bids vary by cost, schedule risk, and finish quality. Cheap is rarely cheap."
              action={<div />}
            />
            <ChoiceList<ContractorKey> value={contractor} onChange={setContractor} options={CONTRACTORS.map((c) => ({
              key: c.key, label: c.name, bullets: c.bullets,
              summary: `Hard-cost mult ×${c.costMult.toFixed(2)} · timeline ${c.timelineDelta >= 0 ? "+" : ""}${c.timelineDelta} mo`,
            }))} />
            <Button onClick={() => setStep("permitting")} disabled={!contractor} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "permitting" && project && (
          <>
            <ModuleStepCard
              icon={<Stamp className="w-8 h-8 text-primary" />}
              title="Permitting Strategy"
              body="Speed, safety, or shortcut? The wrong call here can trigger a stop-work order."
              action={<div />}
            />
            <ChoiceList<PermitKey> value={permit} onChange={setPermit} options={PERMIT_OPTIONS.map((p) => ({
              key: p.key, label: p.name, bullets: p.bullets,
              summary: `Cost ${money(p.cost)} · timeline ${p.timelineDelta >= 0 ? "+" : ""}${p.timelineDelta} mo`,
            }))} />
            <Button onClick={() => setStep("event")} disabled={!permit} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "event" && project && eventInfo && (
          <>
            <ModuleStepCard
              icon={<AlertTriangle className="w-8 h-8 text-destructive" />}
              title={eventInfo.title}
              body={eventInfo.body}
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/60 p-3 space-y-1 text-[11px]">
              <Row label="Extra months" value={`${eventInfo.severity}`} />
              <Row label="Additional interest" value={money(eventInfo.extraInterest)} />
              <Row label="Additional carrying cost" value={money(eventInfo.extraHolding)} />
              <Row label="Contingency budget" value={money(contingencyBudget)} />
            </div>
            <p className="text-[11px] text-muted-foreground italic">
              Severity scales with your earlier diligence, leverage, contingency, and pace.
            </p>
            <Button onClick={() => setStep("response")} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "response" && eventInfo && (
          <>
            <ModuleStepCard
              icon={<FlagTriangleRight className="w-8 h-8 text-primary" />}
              title="How do you respond?"
              body="There's no free response. You're choosing which kind of damage to absorb."
              action={<div />}
            />
            <ChoiceList<ResponseKey> value={resp} onChange={setResp} options={RESPONSES.map((r) => ({
              key: r.key, label: r.name, bullets: r.bullets,
            }))} />
            {resp && <p className="text-[11px] text-foreground/85 italic">{respImpact.note}</p>}
            <Button onClick={() => setStep("cash_check")} disabled={!resp} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "cash_check" && project && (
          <>
            {cashCrunch ? (
              <>
                <ModuleStepCard
                  icon={<AlertTriangle className="w-8 h-8 text-destructive" />}
                  title="Cash Crunch"
                  body={`Cash on hand is ${money(cashAvailable)}. You need a recovery decision before the project can continue.`}
                  action={<div />}
                />
                <ChoiceList<CrunchKey> value={crunch} onChange={setCrunch} options={CRUNCH_OPTIONS.map((c) => ({
                  key: c.key, label: c.name, bullets: c.bullets,
                }))} />
                <Button onClick={() => setStep("ledger")} disabled={!crunch} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <ModuleStepCard
                  icon={<CheckCircle2 className="w-8 h-8 text-emerald-400" />}
                  title="Cash Position Holding"
                  body={`Cash on hand: ${money(cashAvailable)}. You weathered the event without a crunch — but the project isn't free of risk yet.`}
                  action={<Button onClick={() => setStep("ledger")} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>}
                />
              </>
            )}
            <Disclaimer />
          </>
        )}

        {step === "ledger" && project && (
          <>
            <ModuleStepCard
              icon={<Receipt className="w-8 h-8 text-primary" />}
              title="Development Ledger"
              body="Profit is what remains after every cost, fee, delay, and selling expense."
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/60 p-3 space-y-1 text-[11px]">
              <Row label="Acquisition" value={money(project.askingPrice)} />
              <Row label="Hard costs (actual)" value={money(actualHardCost)} />
              <Row label="Other build costs" value={money(otherBuildCost)} />
              <Row label="Due diligence" value={money(ddCost)} />
              <Row label="Permit fees" value={money(permitFees)} />
              <Row label="Financing interest" value={money(interestAccrued)} />
              <Row label="Carrying costs" value={money(carryingCost)} />
              <Row label="Contingency used" value={money(respImpact.contingencyUsed)} />
              <Row label="Rescue / event debt" value={money(respImpact.extraDebt + crunchExtraInterest)} />
              <Row label="Months in project" value={`${totalMonths}`} />
              <div className="border-t border-border my-2" />
              <Row label="Total all-in cost" value={money(allInCost)} bold />
              <Row label="Estimated final value" value={money(finalValueRaw)} />
              <Row label="Selling costs (6%)" value={`-${money(sellingCosts)}`} />
              <Row label="Debt payoff" value={`-${money(debtPayoff)}`} />
              {partnerShare > 0 && <Row label="Partner share (30%)" value={`-${money(Math.round(grossProfit * partnerShare))}`} />}
              <div className="border-t border-border my-2" />
              <Row label="Estimated profit / loss" value={money(profitAfterPartner)} bold
                valueClass={profitAfterPartner >= 0 ? "text-emerald-400" : "text-destructive"} />
              <Row label="Return on equity" value={`${roe.toFixed(1)}%`} />
              <Row label="Risk score" value={`${risk}`} />
            </div>
            <Button onClick={() => setStep("exit")} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "exit" && project && (
          <>
            <ModuleStepCard
              icon={<TrendingUp className="w-8 h-8 text-accent" />}
              title="Choose Your Exit"
              body="Exit options change based on your actual project numbers. Strong, weak, and dangerous are labeled."
              action={<div />}
            />
            <ChoiceList value={exitChoice} onChange={setExitChoice} options={exitOptions} />
            <Button onClick={() => setStep("report")} disabled={!exitChoice} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "report" && project && (
          <>
            <ModuleStepCard
              icon={<Crown className="w-8 h-8 text-primary" />}
              title="Project Outcome Report"
              body={ratingLine(profitAfterPartner, cashCrunch, risk)}
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/60 p-3 space-y-1 text-[11px]">
              <Row label="Developer rating" value={ratingTag(profitAfterPartner, cashCrunch, risk)} />
              <Row label="Final profit / loss" value={money(profitAfterPartner)}
                valueClass={profitAfterPartner >= 0 ? "text-emerald-400" : "text-destructive"} />
              <Row label="Return on equity" value={`${roe.toFixed(1)}%`} />
              <Row label="Cash left" value={money(cashAvailable - Math.max(0, respImpact.extraDebt))} />
              <Row label="Months delayed" value={`${monthsDelayed}`} />
              <Row label="Final risk" value={`${risk}`} />
              <Row label="Best decision" value={strongestDecision(dd, lender, alloc, contractor)} />
              <Row label="Worst decision" value={weakestDecision(dd, lender, alloc, contractor, permit)} />
              <Row label="Biggest hidden cost" value={biggestHiddenCost(interestAccrued, carryingCost, respImpact.extraDebt + crunchExtraInterest)} />
              <Row label="What happens next" value={nextStep(profitAfterPartner, cashCrunch)} />
            </div>
            <Button onClick={() => setStep("quiz")} className="w-full gap-2">Take Knowledge Check <ArrowRight className="w-4 h-4" /></Button>
            <Disclaimer />
          </>
        )}

        {step === "quiz" && (
          <ModuleQuiz
            moduleKey="development"
            sampleSize={5}
            shuffleOptions
            onPass={() => {
              setCompleted(true);
              setStep("summary");
              completeModule("development");
              void trackModule({
                eventType: DEV.FINAL_REPORT, moduleSlug: "development", attemptId: attemptRef.current,
                metadata: {
                  project_type: project?.key, project_cost: totalProjectCost,
                  estimated_value: finalValueRaw, loan_amount: loanAmount,
                  equity_required: equityRequired, interest_rate: interestRate,
                  contingency_reserved: contingencyBudget,
                  contingency_used: respImpact.contingencyUsed,
                  cash_remaining: cashAvailable, timeline_months: monthsPlanned,
                  delay_months: monthsDelayed, risk_score: risk,
                  final_profit_loss: profitAfterPartner, return_on_equity: roe,
                  selected_exit_strategy: exitChoice,
                  developer_rating: ratingTag(profitAfterPartner, cashCrunch, risk),
                },
              });
              void commitModuleAttempt({
                moduleSlug: "development", attemptId: attemptRef.current,
                metadata: { final_profit_loss: profitAfterPartner, risk_score: risk },
              });
            }}
          />
        )}

        {step === "summary" && (
          <div className="space-y-6 text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">Development Complete</h2>
            <p className="text-sm text-muted-foreground">
              You ran a {project?.name ?? "project"} end-to-end. Estimated outcome:{" "}
              <span className={profitAfterPartner >= 0 ? "text-emerald-400" : "text-destructive"}>
                {money(profitAfterPartner)}
              </span>.
            </p>
            <Disclaimer />
            <Button onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
          </div>
        )}
      </div>
    </ModuleShell>
  );
};

/* ─────────────────────────  helpers  ─────────────────────────── */

const money = (n: number) =>
  (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString();

function ratingTag(profit: number, crunch: boolean, risk: number) {
  if (profit < 0 || crunch) return "Project Failed: Cash Crunch";
  if (risk >= 100) return "Risky Builder";
  if (profit >= 200_000 && risk < 60) return "Strategic Operator";
  if (profit >= 75_000 && risk < 80) return "Disciplined Developer";
  if (profit > 0 && risk >= 80) return "Cash Crunch Survivor";
  if (profit > 0 && risk >= 60) return "Overleveraged Optimist";
  return "Barely Survived";
}
function ratingLine(profit: number, crunch: boolean, risk: number) {
  const tag = ratingTag(profit, crunch, risk);
  if (tag.startsWith("Project Failed")) return "Cash ran short. The project survived on paper but burned the equity you'd have used on the next deal.";
  if (tag === "Strategic Operator") return "Strong profit, controlled risk. This is what a seasoned developer looks like.";
  if (tag === "Disciplined Developer") return "Solid result with discipline on leverage and contingency.";
  if (tag === "Cash Crunch Survivor") return "You made it out — but the margin between profit and disaster was thin.";
  if (tag === "Overleveraged Optimist") return "Profit looks nice; the risk path you took could have ended this very differently.";
  if (tag === "Risky Builder") return "High risk, modest reward. A small change in timing or rates could have erased the upside.";
  return "The project closed, but a tighter plan would have produced a much better outcome.";
}
function strongestDecision(
  dd: string | null, lender: LenderKey | null,
  alloc: Record<BudgetCategory, number>, contractor: ContractorKey | null
) {
  if (dd === "full") return "Full due diligence";
  if (alloc.contingency >= 10) return "Healthy contingency reserve";
  if (lender === "bank") return "Conservative bank financing";
  if (contractor === "premium") return "Premium contractor";
  return "Project sized to capital";
}
function weakestDecision(
  dd: string | null, lender: LenderKey | null,
  alloc: Record<BudgetCategory, number>, contractor: ContractorKey | null,
  permit: PermitKey | null
) {
  if (permit === "early") return "Starting before permits cleared";
  if (lender === "aggressive") return "Aggressive debt stack";
  if (alloc.contingency < 5) return "Almost no contingency";
  if (contractor === "informal") return "Informal contractor";
  if (dd === "skip") return "Skipped diligence";
  return "Underestimating delay compounding";
}
function biggestHiddenCost(interest: number, carrying: number, rescue: number) {
  const m = Math.max(interest, carrying, rescue);
  if (m === interest) return `Interest carry · ${money(interest)}`;
  if (m === carrying) return `Holding costs · ${money(carrying)}`;
  return `Rescue debt · ${money(rescue)}`;
}
function nextStep(profit: number, crunch: boolean) {
  if (crunch || profit < 0) return "Rebuild cash before the next project";
  if (profit >= 200_000) return "Reinvest into a larger deal";
  return "Roll proceeds into a similar-sized project";
}

export default DevelopmentSimulator;